import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faHeadset } from '@fortawesome/free-solid-svg-icons';
import Logo from '../components/Logo';

const Grazie: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-lg shadow-lg border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-center">
            <Link to="/" className="flex items-center">
              <Logo className="h-10 md:h-12" />
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-grow flex items-center justify-center py-8 md:py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-3xl"
        >
          {/* Card principale con effetti */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden relative">
            {/* Confetti decorativi */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ y: -100, x: Math.random() * 100 + '%', opacity: 1 }}
                  animate={{ 
                    y: '100vh',
                    rotate: Math.random() * 360,
                    opacity: 0
                  }}
                  transition={{
                    duration: 2 + Math.random() * 2,
                    delay: Math.random() * 0.5,
                    ease: "easeOut"
                  }}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: ['#6366F1', '#EC4899', '#F59E0B'][Math.floor(Math.random() * 3)]
                  }}
                />
              ))}
            </div>

            {/* Header colorato */}
            <div className="bg-gradient-to-r from-primary via-secondary to-accent p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 left-1/4 w-40 h-40 bg-white rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-white rounded-full blur-3xl"></div>
              </div>
              
              <div className="relative z-10">
                {/* Icona di successo animata */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 150 }}
                  className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-6 shadow-2xl"
                >
                  <FontAwesomeIcon 
                    icon={faCheckCircle} 
                    className="text-5xl text-green-500"
                  />
                </motion.div>

                {/* Titolo */}
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-3xl md:text-5xl font-bold text-white mb-3"
                >
                  🎉 Tutto Fatto!
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="text-lg md:text-xl text-white/90"
                >
                  Il tuo consenso è stato registrato con successo
                </motion.p>
              </div>
            </div>

            {/* Contenuto */}
            <div className="p-6 md:p-10">
              {/* Messaggio principale */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-primary/20 rounded-2xl p-8 md:p-12 text-center"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <FontAwesomeIcon icon={faHeadset} className="text-white text-3xl" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                  Grazie per il tuo tempo!
                </h2>
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto">
                  Il nostro operatore è a tua completa disposizione per presentarti le{' '}
                  <strong className="text-primary">migliori offerte personalizzate</strong> su luce, gas e tanto altro.
                </p>
              </motion.div>
            </div>
          </div>

          {/* Badge info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="mt-6 text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg">
              <span className="text-2xl">🔒</span>
              <span className="text-sm font-medium text-gray-700">
                Dati protetti secondo normative GDPR
              </span>
            </div>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-600 mb-2">
            Copyright © 2026 Servizio Clienti Energia
          </p>
          <div className="flex justify-center gap-4 text-xs">
            <Link to="/privacy-policy" className="text-primary hover:text-secondary hover:underline font-medium">
              Privacy Policy
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/cookie-policy" className="text-primary hover:text-secondary hover:underline font-medium">
              Cookie Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Grazie;
